// Angular Modules
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  CustomCollectionHttpResponse,
  CustomHttpResponse,
  Entity,
  HydraCollectionResponse,
  Options,
  SharedEnvironmentConfig,
} from '../models';
import { SHARED_ENVIRONMENT_CONFIG } from '../config';

@Injectable({
  providedIn: 'root',
})
export class ApiHttpService<T extends Entity> {
  private http = inject(HttpClient);
  private config = inject<SharedEnvironmentConfig>(SHARED_ENVIRONMENT_CONFIG);

  options: Options = {} as Options;
  headers: Record<string, string> = {
    'Content-Type': 'application/ld+json; charset=utf-8',
  };

  constructor() {
    this.options.headers = this.headers;
    this.options.observe = 'response' as 'body';
  }

  public get(url: string): Observable<CustomHttpResponse<T>> {
    url = this.config.apiUrl + url;
    return this.http.get(url, this.options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public getMany(url: string): Observable<CustomHttpResponse<HydraCollectionResponse<T>>> {
    url = this.config.apiUrl + url;
    return this.http.get(url, this.options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<HydraCollectionResponse<T>>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public getById(url: string): Observable<CustomHttpResponse<T>> {
    url = this.config.apiUrl + url;
    return this.http.get(url, this.options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public getAll(url: string): Observable<CustomHttpResponse<HydraCollectionResponse<T>>> {
    // the url may already carry a query string (e.g. `/brands?isActive=true`)
    url = this.config.apiUrl + url + (url.includes('?') ? '&' : '?') + 'pagination=false';
    return this.http.get(url, this.options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<HydraCollectionResponse<T>>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public cancel(url: string) {
    url = this.config.apiUrl + url;
    return this.http.post(url, {}, this.options).pipe(
      map((result) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public post(url: string, data?: T) {
    url = this.config.apiUrl + url;
    return this.http.post(url, data ?? {}, this.options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public patch(url: string, data: T) {
    url = this.config.apiUrl + url;
    const options: Options = {} as Options;
    options.observe = 'response' as 'body';
    options.headers = {
      'Content-Type': 'application/merge-patch+json',
    };

    return this.http.patch(url, data, options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  public patchCollection(url: string, data: T): Observable<T[]> {
    url = this.config.apiUrl + url;
    const options: Options = {} as Options;
    options.observe = 'response' as 'body';
    options.headers = {
      'Content-Type': 'application/merge-patch+json',
    };

    return this.http.patch(url, data, options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomCollectionHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
      map((result) => (result.body as HydraCollectionResponse<T>)['hydra:member'] as T[]),
    );
  }

  public partlyPatch(url: string, data: T) {
    url = this.config.apiUrl + url;
    const options: Options = {} as Options;
    options.headers = {
      'Content-Type': 'application/merge-patch+json',
    };
    return this.http.patch(url, data, options).pipe(
      map((result: ArrayBuffer) => result as unknown as T),
      filter((result) => result !== null && result !== undefined),
    );
  }

  // public patch(url: string, data: T) {
  //   if (this.options && this.options.headers) {
  //     this.options.headers['Content-Type'] = 'application/merge-patch+json';
  //   }
  //
  //   url = this.config.API_ENDPOINT + url;
  //   return this.http.patch(url, data, this.options).pipe(
  //     map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
  //     filter((result) => result.body !== null && result.body !== undefined),
  //   );
  // }

  public delete(url: string) {
    url = this.config.apiUrl + url;
    return this.http.delete(url, this.options);
  }

  public showImage(url: string) {
    url = this.config.apiUrl + url;
    // copy the headers: the shared options are used by every other request of this service
    const options = { ...this.options, headers: { ...this.headers, 'Content-Disposition': 'attachment' } };
    return this.http.get(url, options);
  }

  public downloadFile(url: string) {
    url = this.config.apiUrl + url;
    const options = {
      ...this.options,
      headers: {
        ...this.options?.headers,
        'Content-Disposition': 'attachment',
      },
      responseType: 'blob' as 'json',
    };
    return this.http.get(url, options);
  }

  public downloadFileAsBlob(url: string): Observable<HttpResponse<Blob>> {
    url = this.config.apiUrl + url;

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
      // keep headers if you need auth etc.
      headers: this.options?.headers as any,
    });
  }

  public downloadFilePost(url: string, data: T) {
    url = this.config.apiUrl + url;
    const options = {
      ...this.options,
      headers: {
        ...this.options?.headers,
        'Content-Disposition': 'attachment',
      },
      responseType: 'blob' as 'json',
    };

    return this.http.post(url, data, options);
  }

  public postMultiPart(url: string, data: FormData) {
    url = this.config.apiUrl + url;
    const options: Options = {} as Options;
    options.observe = 'response' as 'body';
    if (this.options.headers?.['Authorization']) {
      options.headers = { Authorization: this.options.headers['Authorization'] };
    }

    return this.http.post(url, data, options).pipe(
      map((result: ArrayBuffer) => result as unknown as CustomHttpResponse<T>),
      filter((result) => result.body !== null && result.body !== undefined),
    );
  }

  hasToken(): boolean {
    return !!this.options && !!this.options.headers && !!this.options.headers['Authorization'];
  }
}
